import type { LinearRGB } from '../../core/color';
import type { Vec2, Vec3 } from '../../geo/types';
import type { MeshBuilder, VertexStyle, Frame } from '../mesh/meshBuilder';
import { SURFACE } from '../mesh/attributes';
import { type ShipTemplate, HULL_STATIONS, hullHalfBreadth, sheerHeight } from './shipTemplates';
import { loftSolid, type Ring3 } from './shipSolids';
import { isFormed } from './shipForm';
import { girthFractions, girthLength, plankingStyle } from './shipPatterns';

/**
 * Section rows above the keel: fraction of the keel-to-sheer depth and of the half-breadth.
 * The hull is widest just above the waterline and draws in towards the gunwale (tumblehome).
 */
const SECTION: readonly { readonly depth: number; readonly breadth: number }[] = [
  { depth: 0.2, breadth: 0.6 },
  { depth: 0.5, breadth: 0.92 },
  { depth: 0.8, breadth: 1 },
  { depth: 1, breadth: 0.94 },
];

const GUNWALE_BREADTH = SECTION[SECTION.length - 1]!.breadth;
const KEEL_RISE_EXPONENT = 1.5;

const LEEBOARD = { length: 2, top: 0.5, depth: 1.5, thickness: 0.15, overlap: 0.02 } as const;

/** How far a leeboard stands outside the gunwale. */
export const LEEBOARD_OUTREACH = LEEBOARD.thickness - LEEBOARD.overlap;

const LOCAL: Frame = { origin: [0, 0, 0], yaw: 0 };

export interface HullStation {
  readonly s: number;
  readonly x: number;
  readonly keel: number;
  readonly gunwale: number;
  readonly halfBreadth: number;
}

export function keelHeight(s: number, keelDepth: number): number {
  return -keelDepth * (1 - Math.pow(Math.abs(2 * s - 1), KEEL_RISE_EXPONENT));
}

export function hullStations(template: ShipTemplate): readonly HullStation[] {
  return Array.from({ length: HULL_STATIONS + 1 }, (_, i) => {
    const s = i / HULL_STATIONS;
    return {
      s,
      x: (s - 0.5) * template.length,
      keel: keelHeight(s, template.keelDepth),
      gunwale: sheerHeight(s, template.sheerFore, template.sheerAft),
      halfBreadth: hullHalfBreadth(s, template.beam, template.hullExponent),
    };
  });
}

/**
 * The hull between stations is linear, so anything that must sit on or inside it (leeboards, the
 * mast steps, shroud ends) reads it through this interpolation rather than the smooth curves.
 */
export function stationAt(stations: readonly HullStation[], s: number): HullStation {
  const i = Math.min(Math.floor(s * HULL_STATIONS), HULL_STATIONS - 1);
  const [a, b] = [stations[i]!, stations[i + 1]!];
  const t = (s - a.s) / (b.s - a.s);
  const lerp = (p: number, q: number): number => p + (q - p) * t;
  return {
    s,
    x: lerp(a.x, b.x),
    keel: lerp(a.keel, b.keel),
    gunwale: lerp(a.gunwale, b.gunwale),
    halfBreadth: lerp(a.halfBreadth, b.halfBreadth),
  };
}

export function gunwaleHalfWidth(station: HullStation): number {
  return station.halfBreadth * GUNWALE_BREADTH;
}

/** The keel and the section rows up the starboard side to the gunwale. */
export function starboardSection(st: HullStation): Vec3[] {
  const depth = st.gunwale - st.keel;
  const rows = SECTION.map((row): Vec3 => [
    st.x,
    st.keel + row.depth * depth,
    row.breadth * st.halfBreadth,
  ]);
  return [[st.x, st.keel, 0], ...rows];
}

/* Keel, starboard rows up, across the deck, port rows down: +X × tangent points outboard. */
function hullRing(st: HullStation): Ring3 {
  const [keel, ...starboard] = starboardSection(st);
  const port = [...starboard].reverse().map(([x, y, z]): Vec3 => [x, y, -z]);
  return [keel!, ...starboard, ...port];
}

/* Ring point k at station r in the planking: metres from the stem and the girth fraction from
   the keel round to the gunwale. */
function plankingUv(
  stations: readonly HullStation[],
  length: number
): (r: number, k: number) => Vec2 {
  const fractions = stations.map((st) => girthFractions(starboardSection(st)));
  const n = SECTION.length;
  return (r, k) => [stations[r]!.x + length / 2, fractions[r]![k <= n ? k : 2 * n + 1 - k]!];
}

/** Height of the stem head, where a boat's forestay comes down. */
export function bowTopHeight(stations: readonly HullStation[]): number {
  return stations[0]!.gunwale;
}

function buildLeeboards(builder: MeshBuilder, style: VertexStyle, template: ShipTemplate): void {
  const inner = template.beam / 2 - LEEBOARD.overlap;
  const outer = inner + LEEBOARD.thickness;
  const [x0, x1] = [-LEEBOARD.length / 2, LEEBOARD.length / 2];
  builder.box(style, LOCAL, [x0, -LEEBOARD.depth, inner], [x1, LEEBOARD.top, outer]);
  builder.box(style, LOCAL, [x0, -LEEBOARD.depth, -outer], [x1, LEEBOARD.top, -inner]);
}

/**
 * A boat's closed hull lofted through the PLAN stations (bow at -X), with leeboards as separate
 * closed solids that overlap it; every part faces outward. The big ships have formed hulls
 * (shipHull.ts).
 */
export function buildHull(
  builder: MeshBuilder,
  template: ShipTemplate,
  hullColor: LinearRGB,
  edgeId: number,
  seed: number
): void {
  if (isFormed(template)) throw new Error(`${template.type} has a formed hull (shipHull.ts)`);
  const style: VertexStyle = {
    color: hullColor,
    surface: SURFACE.wood,
    edgeId,
    seed,
    strokeAngle: 0,
    extent: [0, 0],
  };
  const stations = hullStations(template);
  const midship = stationAt(stations, 0.5);
  const planking = plankingStyle(style, template.type, girthLength(starboardSection(midship)));
  loftSolid(
    builder,
    style,
    stations.map(hullRing),
    (k) => (k === SECTION.length ? style : planking),
    plankingUv(stations, template.length)
  );
  if (template.hasLeeboards) buildLeeboards(builder, style, template);
}
